import { Body, Bounds, Sleeping, Vector, Vertices } from 'matter-js'
import gsap, { Power2, Bounce } from 'gsap'
import { Mesh, PlaneGeometry, ShaderMaterial, Vector2, Vector3 } from 'three'
import {
  BODY_LABEL_ADDON_FROM_SLIT,
  MATTER_TO_THREE_SCALE,
  BODY_LABEL_ADDON_REVEALED,
  PhysicsLayerZ
} from './misc/constants'
import { CollisionGroup } from './rooms/room-physics-base'
import { lerp } from '@/shared/misc/math-helpers'
import { container } from 'tsyringe'
import { ITouch } from '@/shared/di/interface/touch'
import { Interface } from '@/shared/di/interface'
import { Asset } from '@/state/vault/domain'
import { Id } from '@/common/base-types'
import revealGlowMaterial from './materials/reveal-glow-material'
import Pool from '@/shared/misc/pool'

const tempVector3 = new Vector3()
let globalOffsetZ = 0

const spriteGeo = new PlaneGeometry(8, 8, 1, 1)

class Sprite extends Mesh {
  constructor() {
    super(spriteGeo, revealGlowMaterial.getMaterial())
    this.userData.enableBloom = false
    this.userData.hideFromBloom = true
    this.userData.isSprite = true
  }
}

const spritePool = new Pool(Sprite)
export default class BaseController {
  assetId: Id = ''
  asset: Asset = {} as Asset
  isZoomed = false
  isAnimated = false
  isLoaded = false
  isLifted = false
  mesh: Mesh = {} as Mesh
  body: Body = {} as Body
  meshInitScale = 1
  physicsLayerInactive = PhysicsLayerZ.Back
  physicsLayerActive = PhysicsLayerZ.Front
  #bodyOriginalVertices: Vector[]
  #bodyInterpolatedVertices: Vector[]
  #verticesLength = 0
  #tweenInstance: gsap.core.Tween | undefined
  #scale: number
  #interpolateAmount = 0
  #depthOffsetZ = 0
  #bounceExtraZ = 0

  pressedTime = Date.now()
  timeSinceLastPress = Date.now()
  releasedTime = Date.now()
  singleTapTween: gsap.core.Tween | undefined
  glowSprite: Sprite | undefined
  lastPosition = new Vector3()

  protected currentTouchIdentifier = -1
  protected previousPointerPosition = new Vector2()
  protected lastTouchesLength = 0

  protected position2D: Vector = {
    x: 0,
    y: 0
  }
  protected initAngle = 0

  constructor() {
    this.#bodyOriginalVertices = []
    this.#bodyInterpolatedVertices = []
    this.#scale = 1

    globalOffsetZ += 0.01
    if (globalOffsetZ > 0.2) {
      globalOffsetZ = 0
    }

    this.#depthOffsetZ = globalOffsetZ
  }

  load(): Promise<void> {
    return Promise.resolve()
  }

  initBody() {
    Body.setAngle(this.body, this.initAngle)

    this.position2D.x = this.position2D.y = 0
    this.#bodyOriginalVertices = this.body.vertices.concat()
    this.#bodyInterpolatedVertices = this.body.vertices.concat()
    this.#verticesLength = this.body.vertices.length
    this.body.collisionFilter.group = CollisionGroup.InactiveCoins

    this.body.plugin.rotateConstraintX = -140
    Body.setPosition(this.body, this.position2D)
  }

  animateInBody() {
    this.#interpolateAmount = 1
    const animObject = {
      progress: 0
    }

    if (this.#tweenInstance) {
      this.#tweenInstance.kill()
      this.#tweenInstance = undefined
    }
    this.#tweenInstance = gsap.fromTo(
      animObject,
      { progress: 0.01 },
      {
        progress: 1,
        duration: 0.3,
        //ease: Sine.easeOut,
        onUpdate: () => {
          this.#scale = animObject.progress
          this.setVerticesAtScale(this.#scale)
        },
        onComplete: () => {
          this.#tweenInstance = undefined
        }
      }
    )
  }

  setVerticesAtScale(scale: number) {
    for (let i = 0; i < this.#verticesLength; i++) {
      this.#bodyInterpolatedVertices[i] = Vector.mult(this.#bodyOriginalVertices[i], scale)
    }

    //Body.setVertices(this.#body, this.#bodyInterpolatedVertices)

    Vertices.rotate(this.body.vertices, -(this.body.angle - this.initAngle), this.body.position)

    // change vertices
    this.body.vertices = Vertices.create(this.#bodyInterpolatedVertices, this.body)

    // update properties
    //this.body.axes = Axes.fromVertices(this.body.vertices)
    this.body.area = Vertices.area(this.body.vertices, true)

    //disable this line (compared to the original)
    //Body.setMass(this.#body, this.body.density * this.body.area)

    // orient vertices around the centre of mass at origin (0, 0)
    const centre = Vertices.centre(this.body.vertices)
    Vertices.translate(this.body.vertices, centre, -1)

    // update inertia while vertices are at origin (0, 0)
    //disable this line (compared to the original)
    //Body.setInertia(this.#body, Body._inertiaScale * Vertices.inertia(this.body.vertices, this.body.mass))

    // update geometry
    Vertices.translate(this.body.vertices, this.body.position, 1)
    Bounds.update(this.body.bounds, this.body.vertices, this.body.velocity)

    Vertices.rotate(this.body.vertices, this.body.angle - this.initAngle, this.body.position)
  }

  setPosition(x: number, y: number) {
    if (this.isAnimated) {
      return
    }
    this.position2D.x = x
    this.position2D.y = y

    Body.setPosition(this.body, this.position2D)

    let toZ =
      this.body.collisionFilter.group === CollisionGroup.ActiveCoins ||
      this.body.label.includes(BODY_LABEL_ADDON_FROM_SLIT)
        ? PhysicsLayerZ.Front
        : 0

    if (this.body.label.includes(BODY_LABEL_ADDON_REVEALED)) {
      toZ = PhysicsLayerZ.Revealed
    }

    tempVector3.set(this.body.position.x * MATTER_TO_THREE_SCALE, -this.body.position.y * MATTER_TO_THREE_SCALE, toZ)
    this.mesh.position.copy(tempVector3)
  }

  addGlowSprite() {
    this.glowSprite = spritePool.get()

    this.glowSprite.scale.setScalar(1 / this.mesh.scale.x)
    this.glowSprite.rotation.x = Math.PI * -0.5
    this.glowSprite.position.y = -1.5 * (1 / this.mesh.scale.x)

    const mat = this.glowSprite.material as ShaderMaterial
    gsap.fromTo(mat.uniforms.opacity, { value: 0 }, { value: 0.5, duration: 0.5 })
    this.mesh.add(this.glowSprite)
  }

  removeGlowSprite(): Promise<void> {
    return new Promise(resolve => {
      if (this.glowSprite) {
        const mat = this.glowSprite.material as ShaderMaterial
        gsap.to(mat.uniforms.opacity, {
          duration: 0.3,
          value: 0,
          onComplete: () => {
            if (this.glowSprite) {
              this.mesh.remove(this.glowSprite)
              spritePool.release(this.glowSprite)
            }

            this.glowSprite = undefined
            resolve()
          }
        })
      } else {
        resolve()
      }
    })
  }

  press(): void {
    this.timeSinceLastPress = Date.now() - this.pressedTime
    this.pressedTime = Date.now()
  }

  release(): void {
    this.releasedTime = Date.now()
  }

  bounce(dist: number, angle: number) {
    if (this.#bounceExtraZ > 0.1) {
      return
    }

    const tweenValues = {
      y: this.#bounceExtraZ
    }

    const randomRot = angle * 0.8

    gsap.to(tweenValues, {
      y: Math.max(0, (1 - dist / 3) * 1.5 * (this.body.density / 0.003)),
      duration: 0.1,
      ease: Power2.easeOut,
      onComplete: () => {
        gsap.to(tweenValues, {
          y: 0,
          duration: 0.4,
          ease: Bounce.easeOut,
          onUpdate: () => {
            this.#bounceExtraZ = tweenValues.y
            this.mesh.rotation.z = tweenValues.y * randomRot
          }
        })
      },
      onUpdate: () => {
        this.#bounceExtraZ = tweenValues.y
        this.mesh.rotation.z = tweenValues.y * randomRot
      }
    })
  }

  removeTouchListener() {
    const touch = container.resolve<ITouch>(Interface.ITouch)
    touch.onTouchesChanged.remove(this.onTouchesChanged)
  }
  addTouchListener() {
    this.removeTouchListener()
    const touch = container.resolve<ITouch>(Interface.ITouch)
    touch.onTouchesChanged.add(this.onTouchesChanged)
  }

  onTouchesChanged = (touches: Touch[]) => {}

  update(time: number, delta: number): void {
    if (this.#interpolateAmount > 0) {
      this.#interpolateAmount -= 0.03
    }

    this.lastPosition.copy(this.mesh.position)

    this.#interpolateAmount = Math.max(0, this.#interpolateAmount)

    //framerate independent lerp
    this.mesh.position.x = lerp(
      this.mesh.position.x,
      this.body.position.x * MATTER_TO_THREE_SCALE,
      1 - this.#interpolateAmount,
      delta
    )
    this.mesh.position.y = lerp(
      this.mesh.position.y,
      -this.body.position.y * MATTER_TO_THREE_SCALE,
      1 - this.#interpolateAmount,
      delta
    )

    let toZ = this.getNeutralZ()

    //const fromSlitState = this.body.label.includes(BODY_LABEL_ADDON_FROM_SLIT)
    //let toZ = this.body.collisionFilter.group === CollisionGroup.ActiveCoins || fromSlitState ? PhysicsLayerZ.Front : 0

    if (this.body.label.includes(BODY_LABEL_ADDON_REVEALED)) {
      toZ = PhysicsLayerZ.Revealed
    }

    if (this.body.plugin.pinchDistance) {
      toZ += (this.body.plugin.pinchDistance / 100) * 2
    }

    if (this.#interpolateAmount > 0.01) {
      this.mesh.position.z = lerp(this.mesh.position.z, toZ + this.#depthOffsetZ, 0.2, delta)
    } else {
      toZ += this.#bounceExtraZ
      this.mesh.position.z = toZ + this.#depthOffsetZ
    }

    if (this.glowSprite) {
      const mat = this.glowSprite.material as ShaderMaterial
      mat.uniforms.time.value = time
    }
  }

  protected getNeutralZ(): number {
    return this.body.collisionFilter.group === CollisionGroup.ActiveCoins ||
      this.body.label.includes(BODY_LABEL_ADDON_FROM_SLIT)
      ? this.physicsLayerActive
      : this.physicsLayerInactive
  }

  get isBodyAnimating() {
    return this.#tweenInstance !== undefined
  }

  //override in sub-class
  get baseColor() {
    return 0x000000
  }

  enable() {
    this.mesh.visible = true
    this.body.isSensor = false
    Sleeping.set(this.body, false)
  }

  disable() {
    this.mesh.visible = false
    this.body.isSensor = true
    Sleeping.set(this.body, true)
  }

  destroy() {
    const mesh = this.mesh as Mesh
    if (mesh.parent) {
      mesh.parent.remove(mesh)
    }

    const touch = container.resolve<ITouch>(Interface.ITouch)
    touch.onTouchesChanged.remove(this.onTouchesChanged)

    //const mat = mesh.material as MeshStandardMaterial
    //mat.dispose()
  }

  get id() {
    return this.body.id
  }
}
